import React, { useCallback, useEffect, useState } from 'react';
import AuthService from '../../services/AuthService';
import NotificationEngineService from '../../services/NotificationEngineService';
import ClientService from '../../../SuperAdmin/services/ClientService';

const CHANNELS = [
  { id: 1, label: 'In-App', short: 'App', color: '#5e72e4' },
  { id: 2, label: 'Email', short: 'Email', color: '#11cdef' },
  { id: 3, label: 'WhatsApp', short: 'WA', color: '#2dce89' },
  { id: 4, label: 'SMS', short: 'SMS', color: '#8965e0' },
  { id: 5, label: 'Push', short: 'Push', color: '#ffd600' },
  { id: 6, label: 'Webhook', short: 'Hook', color: '#adb5bd' },
];

const QUEUE_STATUSES = [
  { id: 1, label: 'Pending', color: '#fb6340' },
  { id: 2, label: 'Processing', color: '#11cdef' },
  { id: 3, label: 'Completed', color: '#2dce89' },
  { id: 4, label: 'Failed', color: '#f5365c' },
  { id: 5, label: 'Cancelled', color: '#8898aa' },
];

const STATUS_NAME_TO_ID = {
  Pending: 1,
  Processing: 2,
  Completed: 3,
  Failed: 4,
  Cancelled: 5,
};

const CHANNEL_NAME_TO_ID = {
  InApp: 1,
  Email: 2,
  WhatsApp: 3,
  Sms: 4,
  Push: 5,
  Webhook: 6,
};

/** Normalize API enum value (number or string name) to numeric id. */
function enumId(value, nameMap = STATUS_NAME_TO_ID) {
  if (value == null || value === '') return null;
  if (typeof value === 'string' && nameMap[value] != null) return nameMap[value];
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

function channelMeta(value) {
  const id = enumId(value, CHANNEL_NAME_TO_ID);
  return CHANNELS.find((ch) => ch.id === id) ?? { id, label: value ?? '—', color: '#8898aa' };
}

function statusMeta(value) {
  const id = enumId(value);
  return QUEUE_STATUSES.find((s) => s.id === id) ?? { id, label: value ?? '—', color: '#8898aa' };
}

const enumBadge = (label, color) => (
  <span style={{
    display: 'inline-block',
    padding: '3px 8px',
    borderRadius: 6,
    background: `${color}18`,
    color,
    fontSize: 11,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  }}
  >
    {label}
  </span>
);

const TABS = [
  ['dashboard', 'Dashboard', 'fa-chart-line'],
  ['schedulers', 'Schedulers', 'fa-clock'],
  ['templates', 'Templates', 'fa-file-alt'],
  ['queue', 'Queue', 'fa-inbox'],
  ['history', 'History', 'fa-history'],
];

const pageBg = { padding: '16px 12px 32px', minHeight: '100vh', background: '#f4f6fb', width: '100%', boxSizing: 'border-box' };
const card = {
  background: '#fff',
  borderRadius: 14,
  padding: '16px 18px',
  boxShadow: '0 2px 12px rgba(52,71,103,0.07)',
  border: '1px solid rgba(233,236,239,0.8)',
};
const inp = {
  width: '100%',
  padding: '6px 8px',
  borderRadius: 8,
  border: '1px solid #e9ecef',
  fontSize: 12,
  color: '#344767',
  background: '#fff',
};
const th = { padding: '10px 12px', fontSize: 11, fontWeight: 700, color: '#8898aa', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' };
const td = { padding: '8px 12px', fontSize: 13, color: '#344767', verticalAlign: 'middle' };

/** API may return channels as array, `{ $values: [] }`, or JSON string. */
function normalizeChannels(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw.map(Number).filter((n) => !Number.isNaN(n));
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return normalizeChannels(parsed);
    } catch {
      return [];
    }
  }
  if (Array.isArray(raw.$values)) return raw.$values.map(Number).filter((n) => !Number.isNaN(n));
  return [];
}

export default function NotificationEngineAdmin() {
  const roleNum = parseInt(AuthService.getLoggedInRoleId(), 10);
  const isSuperAdmin = roleNum === 1;
  const defaultClientId = parseInt(AuthService.getLoggedInClientId(), 10) || 0;

  const [tab, setTab] = useState('dashboard');
  const [clientId, setClientId] = useState(defaultClientId);
  const [clients, setClients] = useState([]);
  const [settings, setSettings] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [queue, setQueue] = useState([]);
  const [history, setHistory] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 4000);
  };

  useEffect(() => {
    if (isSuperAdmin) {
      ClientService.getAllCLients().then((list) => {
        setClients(list);
        if ((!clientId || clientId <= 0) && list.length) {
          setClientId(list[0].id);
        }
      }).catch(() => {});
    }
  }, [isSuperAdmin]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const data = await NotificationEngineService.getDashboard(clientId || undefined);
      setDashboard(data);
    } catch {
      showMsg('Could not load dashboard.', 'error');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const loadSettings = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const data = await NotificationEngineService.getSettings(clientId);
      const list = Array.isArray(data) ? data : [];
      setSettings(
        list.map((s) => {
          const rp = s.retryPolicy ?? s.RetryPolicy ?? {};
          return {
            ...s,
            channels: normalizeChannels(s.channels ?? s.Channels),
            retryPolicy: {
              maxAttempts: rp.maxAttempts ?? rp.MaxAttempts ?? 3,
              delaysMinutes: normalizeChannels(rp.delaysMinutes ?? rp.DelaysMinutes) || [5, 30],
            },
          };
        })
      );
    } catch {
      showMsg('Could not load scheduler settings.', 'error');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const data = await NotificationEngineService.getQueue(clientId || undefined);
      setQueue(Array.isArray(data) ? data : []);
    } catch {
      showMsg('Could not load queue.', 'error');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const data = await NotificationEngineService.getHistory(clientId || undefined);
      setHistory(Array.isArray(data) ? data : []);
    } catch {
      showMsg('Could not load history.', 'error');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const data = await NotificationEngineService.getTemplates(clientId || undefined);
      setTemplates(Array.isArray(data) ? data : []);
    } catch {
      showMsg('Could not load templates.', 'error');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (tab === 'dashboard') loadDashboard();
    if (tab === 'schedulers') loadSettings();
    if (tab === 'queue') loadQueue();
    if (tab === 'history') loadHistory();
    if (tab === 'templates') loadTemplates();
  }, [tab, clientId, loadDashboard, loadSettings, loadQueue, loadHistory, loadTemplates]);

  const updateSetting = (idx, patch) => {
    setSettings((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const toggleChannel = (idx, channelId) => {
    setSettings((prev) => prev.map((s, i) => {
      if (i !== idx) return s;
      const channels = [...normalizeChannels(s.channels ?? s.Channels)];
      const pos = channels.indexOf(channelId);
      if (pos >= 0) channels.splice(pos, 1);
      else channels.push(channelId);
      return { ...s, channels };
    }));
  };

  const saveSetting = async (setting) => {
    const id = setting.id ?? setting.Id;
    setSavingId(id);
    try {
      await NotificationEngineService.saveSetting(setting, clientId);
      showMsg('Scheduler settings saved.');
      loadSettings();
    } catch (err) {
      const apiMsg = err?.response?.data?.message ?? err?.response?.data?.title;
      showMsg(apiMsg ? `Save failed: ${apiMsg}` : 'Save failed.', 'error');
    } finally {
      setSavingId(null);
    }
  };

  const stat = (label, value, color = '#5e72e4', icon = 'fa-bell') => (
    <div style={{
      padding: '14px 16px',
      borderRadius: 12,
      background: `linear-gradient(135deg, ${color}12, ${color}06)`,
      border: `1px solid ${color}22`,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      minWidth: 0,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, background: `${color}18`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', color, flexShrink: 0,
      }}>
        <i className={`fas ${icon}`} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11, color: '#8898aa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#344767', lineHeight: 1.2 }}>{value ?? '—'}</div>
      </div>
    </div>
  );

  const channelPills = (idx, channels) => (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'nowrap' }}>
      {CHANNELS.map((ch) => {
        const on = channels.includes(ch.id);
        return (
          <button
            key={ch.id}
            type="button"
            title={ch.label}
            onClick={() => toggleChannel(idx, ch.id)}
            style={{
              padding: '3px 8px',
              borderRadius: 6,
              border: `1px solid ${on ? ch.color : '#e9ecef'}`,
              background: on ? `${ch.color}18` : '#f8f9fc',
              color: on ? ch.color : '#adb5bd',
              fontSize: 10,
              fontWeight: 700,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {ch.short}
          </button>
        );
      })}
    </div>
  );

  return (
    <div style={pageBg}>
      <style>{`
        .ne-tab:hover { background: #e9ecef !important; }
        .ne-tab.active { background: linear-gradient(135deg,#5e72e4,#825ee4) !important; color: #fff !important; box-shadow: 0 4px 12px rgba(94,114,228,0.35); }
        .ne-inp:focus { border-color: #5e72e4 !important; box-shadow: 0 0 0 3px rgba(94,114,228,0.12); outline: none; }
        .ne-save:hover { opacity: 0.9; transform: translateY(-1px); }
        .ne-row:hover { background: #fafbfe; }
        .ne-table-wrap { overflow: auto; max-height: calc(100vh - 260px); }
        .ne-table-wrap thead th { position: sticky; top: 0; z-index: 2; background: #f6f9fc; box-shadow: 0 1px 0 #e9ecef; }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 240 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 14,
            background: 'linear-gradient(135deg, #5e72e4 0%, #825ee4 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 20, boxShadow: '0 4px 14px rgba(94,114,228,0.35)', flexShrink: 0,
          }}>
            <i className="fas fa-bell" />
          </div>
          <div>
            <h4 style={{ margin: 0, fontWeight: 800, color: '#344767', fontSize: 22 }}>Notification Engine</h4>
            <p style={{ margin: '2px 0 0', color: '#8898aa', fontSize: 13 }}>
              Monitor schedulers, templates, queue &amp; delivery. Platform worker switches are under Platform settings.
            </p>
          </div>
        </div>

        {isSuperAdmin && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: '#344767' }}>
              <i className="fas fa-building" style={{ marginRight: 6, color: '#5e72e4' }} />
              Agency
            </span>
            <select
              value={clientId}
              onChange={(e) => setClientId(parseInt(e.target.value, 10))}
              className="ne-inp"
              style={{ ...inp, width: 'auto', minWidth: 200, padding: '8px 12px' }}
            >
              {clients.map((c) => (
                <option key={c.id ?? c.Id} value={c.id ?? c.Id}>{c.agencyName ?? c.AgencyName}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {msg && (
        <div style={{
          padding: '10px 14px', borderRadius: 10, marginBottom: 14,
          background: msg.type === 'error' ? '#fff5f5' : '#f0fff4',
          color: msg.type === 'error' ? '#c53030' : '#276749',
          fontWeight: 600, fontSize: 13,
          border: `1px solid ${msg.type === 'error' ? '#fed7d7' : '#c6f6d5'}`,
        }}>
          <i className={`fas ${msg.type === 'error' ? 'fa-exclamation-circle' : 'fa-check-circle'}`} style={{ marginRight: 8 }} />
          {msg.text}
        </div>
      )}

      {/* Tabs + content shell */}
      <div style={card}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid #f0f2f5' }}>
          {TABS.map(([key, label, icon]) => (
            <button
              key={key}
              type="button"
              className={`ne-tab${tab === key ? ' active' : ''}`}
              onClick={() => setTab(key)}
              style={{
                padding: '8px 16px',
                borderRadius: 10,
                border: 'none',
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
                background: tab === key ? undefined : '#f0f2f5',
                color: tab === key ? undefined : '#344767',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                transition: 'all 0.15s ease',
              }}
            >
              <i className={`fas ${icon}`} style={{ fontSize: 12 }} />
              {label}
              {key === 'queue' && queue.length > 0 && tab !== 'queue' && (
                <span style={{ background: '#fb6340', color: '#fff', borderRadius: 999, padding: '1px 6px', fontSize: 10 }}>{queue.length}</span>
              )}
            </button>
          ))}
          {loading && (
            <span style={{ marginLeft: 'auto', color: '#8392ab', fontSize: 13, alignSelf: 'center' }}>
              <i className="fas fa-spinner fa-spin" /> Loading…
            </span>
          )}
        </div>

        {tab === 'dashboard' && dashboard && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
              {stat('Queue length', dashboard.queueLength, '#5e72e4', 'fa-inbox')}
              {stat('Pending', dashboard.pendingCount, '#fb6340', 'fa-hourglass-half')}
              {stat('Failed', dashboard.failedCount, '#f5365c', 'fa-times-circle')}
              {stat('Completed today', dashboard.completedTodayCount, '#2dce89', 'fa-check-circle')}
              {stat('Email success', `${dashboard.emailSuccessRate ?? 0}%`, '#11cdef', 'fa-envelope')}
              {stat('WhatsApp success', `${dashboard.whatsAppSuccessRate ?? 0}%`, '#8965e0', 'fa-comment-dots')}
            </div>
            {isSuperAdmin && (
              <button
                type="button"
                onClick={async () => {
                  try {
                    await NotificationEngineService.runSchedulers(clientId || undefined);
                    showMsg('Schedulers executed.');
                    loadDashboard();
                  } catch {
                    showMsg('Run failed.', 'error');
                  }
                }}
                style={{
                  padding: '10px 20px', borderRadius: 10, border: 'none',
                  background: 'linear-gradient(135deg,#5e72e4,#825ee4)',
                  color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13,
                }}
              >
                <i className="fas fa-play" style={{ marginRight: 8 }} />
                Run all schedulers now
              </button>
            )}
          </div>
        )}

        {tab === 'schedulers' && !loading && settings.length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 16px', color: '#8898aa' }}>
            <i className="fas fa-clock" style={{ fontSize: 32, marginBottom: 12, opacity: 0.4 }} />
            <p style={{ margin: 0 }}>
              {clientId
                ? 'No scheduler settings found. Select another agency or refresh the page.'
                : 'Select an agency above to load scheduler settings.'}
            </p>
          </div>
        )}

        {tab === 'schedulers' && settings.length > 0 && (
          <div className="ne-table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead>
                <tr style={{ textAlign: 'left' }}>
                  <th style={th}>Scheduler</th>
                  <th style={{ ...th, width: 70 }}>On</th>
                  <th style={th}>Channels</th>
                  <th style={{ ...th, width: 100 }}>Reminder</th>
                  <th style={{ ...th, width: 80 }}>Repeat</th>
                  <th style={{ ...th, width: 90 }}>Hours</th>
                  <th style={{ ...th, width: 72 }} />
                </tr>
              </thead>
              <tbody>
                {settings.map((s, idx) => {
                  const rowId = s.id ?? s.Id ?? idx;
                  const name = s.schedulerName ?? s.SchedulerName ?? s.schedulerCode ?? s.SchedulerCode;
                  const code = s.schedulerCode ?? s.SchedulerCode;
                  const enabled = s.isEnabled ?? s.IsEnabled;
                  const channels = normalizeChannels(s.channels ?? s.Channels);
                  const bizHours = !!(s.businessHoursOnly ?? s.BusinessHoursOnly);
                  const isSaving = savingId === rowId;
                  return (
                    <tr key={rowId} className="ne-row" style={{ borderBottom: '1px solid #f0f2f5' }}>
                      <td style={td}>
                        <div style={{ fontWeight: 700, color: '#344767', fontSize: 13 }}>{name}</div>
                        <div style={{ fontSize: 11, color: '#adb5bd', marginTop: 2 }}>{code}</div>
                      </td>
                      <td style={td}>
                        <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={!!enabled}
                            onChange={(e) => updateSetting(idx, { isEnabled: e.target.checked })}
                            style={{ width: 16, height: 16, accentColor: '#5e72e4' }}
                          />
                        </label>
                      </td>
                      <td style={td}>{channelPills(idx, channels)}</td>
                      <td style={td}>
                        <input
                          type="number"
                          className="ne-inp"
                          title="Minutes before"
                          value={s.reminderMinutesBefore ?? s.ReminderMinutesBefore ?? 30}
                          onChange={(e) => updateSetting(idx, { reminderMinutesBefore: parseInt(e.target.value, 10) })}
                          style={{ ...inp, maxWidth: 80 }}
                        />
                      </td>
                      <td style={td}>
                        <input
                          type="number"
                          className="ne-inp"
                          title="Repeat interval (days)"
                          value={s.repeatIntervalDays ?? s.RepeatIntervalDays ?? 2}
                          onChange={(e) => updateSetting(idx, { repeatIntervalDays: parseInt(e.target.value, 10) })}
                          style={{ ...inp, maxWidth: 64 }}
                        />
                      </td>
                      <td style={td}>
                        <button
                          type="button"
                          onClick={() => updateSetting(idx, { businessHoursOnly: !bizHours })}
                          title="Business hours only"
                          style={{
                            padding: '4px 10px',
                            borderRadius: 6,
                            border: `1px solid ${bizHours ? '#5e72e4' : '#e9ecef'}`,
                            background: bizHours ? '#5e72e414' : '#f8f9fc',
                            color: bizHours ? '#5e72e4' : '#adb5bd',
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {bizHours ? '9–7' : '24/7'}
                        </button>
                      </td>
                      <td style={td}>
                        <button
                          type="button"
                          className="ne-save"
                          disabled={isSaving}
                          onClick={() => saveSetting(s)}
                          style={{
                            padding: '5px 12px',
                            borderRadius: 8,
                            border: 'none',
                            background: isSaving ? '#adb5bd' : 'linear-gradient(135deg,#5e72e4,#825ee4)',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: 11,
                            cursor: isSaving ? 'wait' : 'pointer',
                            whiteSpace: 'nowrap',
                            transition: 'all 0.15s ease',
                          }}
                        >
                          {isSaving ? '…' : 'Save'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'templates' && (
          <div>
            <p style={{ color: '#8898aa', fontSize: 12, margin: '0 0 12px' }}>
              Variables: {'{{LeadName}}'}, {'{{LeadCode}}'}, {'{{ExecutiveName}}'}, {'{{PropertyName}}'}, {'{{VisitDate}}'}, {'{{Amount}}'}, {'{{DueDate}}'}, {'{{FollowUpDate}}'}
            </p>
            <div className="ne-table-wrap">
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                <thead>
                  <tr style={{ textAlign: 'left' }}>
                    <th style={th}>Code</th>
                    <th style={th}>Name</th>
                    <th style={th}>Channel</th>
                    <th style={th}>Subject</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((t) => {
                    const channel = channelMeta(t.channel ?? t.Channel);
                    return (
                    <tr key={t.id ?? t.Id} className="ne-row" style={{ borderBottom: '1px solid #f0f2f5' }}>
                      <td style={td}><code style={{ fontSize: 11, background: '#f6f9fc', padding: '2px 6px', borderRadius: 4 }}>{t.code ?? t.Code}</code></td>
                      <td style={td}>{t.name ?? t.Name}</td>
                      <td style={td}>{enumBadge(channel.label, channel.color)}</td>
                      <td style={{ ...td, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject ?? t.Subject}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'queue' && (
          <div className="ne-table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
              <thead>
                <tr style={{ textAlign: 'left' }}>
                  <th style={th}>ID</th>
                  <th style={th}>Scheduler</th>
                  <th style={th}>Channel</th>
                  <th style={th}>Status</th>
                  <th style={th}>Scheduled</th>
                  <th style={th}>Subject</th>
                  <th style={{ ...th, width: 70 }} />
                </tr>
              </thead>
              <tbody>
                {queue.map((q) => {
                  const statusVal = q.status ?? q.Status;
                  const status = statusMeta(statusVal);
                  const channel = channelMeta(q.channel ?? q.Channel);
                  return (
                  <tr key={q.id ?? q.Id} className="ne-row" style={{ borderBottom: '1px solid #f0f2f5' }}>
                    <td style={td}>{q.id ?? q.Id}</td>
                    <td style={td}>{q.schedulerDefinitionCode ?? q.SchedulerDefinitionCode}</td>
                    <td style={td}>{enumBadge(channel.label, channel.color)}</td>
                    <td style={td}>{enumBadge(status.label, status.color)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{new Date(q.scheduledFor ?? q.ScheduledFor).toLocaleString()}</td>
                    <td style={{ ...td, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{q.subject ?? q.Subject}</td>
                    <td style={td}>
                      {enumId(statusVal) === 4 && (
                        <button
                          type="button"
                          onClick={async () => { await NotificationEngineService.retry(q.id ?? q.Id); showMsg('Retry queued.'); loadQueue(); }}
                          style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: '#fb6340', color: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}
                        >
                          Retry
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'history' && (
          <div className="ne-table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
              <thead>
                <tr style={{ textAlign: 'left' }}>
                  <th style={th}>Channel</th>
                  <th style={th}>Status</th>
                  <th style={th}>Sent</th>
                  <th style={th}>Scheduler</th>
                  <th style={th}>Failure</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => {
                  const channel = channelMeta(h.channel ?? h.Channel);
                  const status = statusMeta(h.status ?? h.Status);
                  return (
                  <tr key={h.id ?? h.Id} className="ne-row" style={{ borderBottom: '1px solid #f0f2f5' }}>
                    <td style={td}>{enumBadge(channel.label, channel.color)}</td>
                    <td style={td}>{enumBadge(status.label, status.color)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{h.sentAt ?? h.SentAt ? new Date(h.sentAt ?? h.SentAt).toLocaleString() : '—'}</td>
                    <td style={td}>{h.schedulerDefinitionCode ?? h.SchedulerDefinitionCode}</td>
                    <td style={{ ...td, color: '#f5365c', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.failureReason ?? h.FailureReason ?? '—'}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
